import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { tenantAdminService } from "@/services/tenant-admin-service";
import { platformPermissionsService } from "@/services/platform-permissions-service";
import { guesteriaAdminKeys } from "@/components/pages/users/hooks/useGuesteriaAdmins";
import { TENANT_PERMISSION_CATALOG } from "@/components/pages/users/constants/tenantPermissionCatalog";
import type { PermissionCatalogItem } from "@/types/permission-catalog";
import type { PlatformCustomRole } from "@/types/platform-role";
import type {
  CustomRoleListQuery,
  CreateTenantCustomRoleDto,
  TenantCustomRole,
  UpdateTenantCustomRoleDto,
} from "@/types/user";

export type CustomRoleScope = "tenant" | "platform";
export type CustomRoleListItem = TenantCustomRole | PlatformCustomRole;
export type CreateCustomRolePayload = Omit<
  CreateTenantCustomRoleDto,
  "baseRole"
> & {
  baseRole?: CreateTenantCustomRoleDto["baseRole"];
};
export type UpdateCustomRolePayload = UpdateTenantCustomRoleDto;

export const customRoleKeys = {
  all: ["custom-roles"] as const,
  lists: (scope: CustomRoleScope = "tenant") =>
    [...customRoleKeys.all, scope, "list"] as const,
  list: (scope: CustomRoleScope, query?: CustomRoleListQuery) =>
    [...customRoleKeys.lists(scope), query] as const,
  permissionCatalog: (scope: CustomRoleScope) =>
    [...customRoleKeys.all, scope, "permission-catalog"] as const,
  userAssignments: (userId: string) =>
    [...customRoleKeys.all, "user-assignments", userId] as const,
  platformUserAssignments: (userId: string) =>
    [...customRoleKeys.all, "platform-user-assignments", userId] as const,
};

const resolveCustomRolesArgs = (
  scopeOrEnabled: CustomRoleScope | boolean = "tenant",
  enabled: boolean = true,
): { scope: CustomRoleScope; enabled: boolean } => {
  if (typeof scopeOrEnabled === "boolean") {
    return { scope: "tenant", enabled: scopeOrEnabled };
  }

  return { scope: scopeOrEnabled, enabled };
};

export function useCustomRoles(
  scopeOrEnabled?: boolean,
): UseQueryResult<TenantCustomRole[], Error>;
export function useCustomRoles(
  scopeOrEnabled: "tenant",
  enabled?: boolean,
  query?: CustomRoleListQuery,
): UseQueryResult<TenantCustomRole[], Error>;
export function useCustomRoles(
  scopeOrEnabled: "platform",
  enabled?: boolean,
  query?: CustomRoleListQuery,
): UseQueryResult<PlatformCustomRole[], Error>;
export function useCustomRoles(
  scopeOrEnabled: CustomRoleScope,
  enabled?: boolean,
  query?: CustomRoleListQuery,
): UseQueryResult<CustomRoleListItem[], Error>;
export function useCustomRoles(
  scopeOrEnabled: CustomRoleScope | boolean = "tenant",
  enabled: boolean = true,
  query?: CustomRoleListQuery,
): UseQueryResult<CustomRoleListItem[], Error> {
  const args = resolveCustomRolesArgs(scopeOrEnabled, enabled);

  return useQuery<CustomRoleListItem[]>({
    queryKey: customRoleKeys.list(args.scope, query),
    queryFn: () =>
      args.scope === "platform"
        ? platformPermissionsService.getRoles(query)
        : tenantAdminService.getCustomRoles(query),
    staleTime: 5 * 60 * 1000,
    enabled: args.enabled,
  });
}

export const useRolePermissionCatalog = (
  scope: CustomRoleScope,
  enabled = true,
) =>
  useQuery({
    queryKey: customRoleKeys.permissionCatalog(scope),
    queryFn: (): Promise<PermissionCatalogItem[]> =>
      scope === "platform"
        ? platformPermissionsService.getPermissions()
        : Promise.resolve(TENANT_PERMISSION_CATALOG),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useCreateCustomRole = (scope: CustomRoleScope = "tenant") => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCustomRolePayload) => {
      if (scope === "platform") {
        return platformPermissionsService.createRole({
          name: payload.name,
          description: payload.description,
          permissionKeys: payload.permissionKeys,
        });
      }

      return tenantAdminService.createCustomRole(
        payload as CreateTenantCustomRoleDto,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.lists(scope) });
    },
  });
};

export const useUpdateCustomRole = (scope: CustomRoleScope = "tenant") => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateCustomRolePayload;
    }) =>
      scope === "platform"
        ? platformPermissionsService.updateRole(id, payload)
        : tenantAdminService.updateCustomRole(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.lists(scope) });
    },
  });
};

export const useDeleteCustomRole = (scope: CustomRoleScope = "tenant") => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      scope === "platform"
        ? platformPermissionsService.deleteRole(id)
        : tenantAdminService.deleteCustomRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.lists(scope) });
    },
  });
};

export const useUserCustomRoleAssignments = (userId: string, enabled = true) =>
  useQuery({
    queryKey: customRoleKeys.userAssignments(userId),
    queryFn: () => tenantAdminService.getUserCustomRoles(userId),
    enabled: Boolean(userId) && enabled,
    staleTime: 60 * 1000,
  });

export const useReplaceUserCustomRoles = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customRoleIds: string[]) =>
      tenantAdminService.replaceUserCustomRoles(userId, customRoleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customRoleKeys.userAssignments(userId),
      });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
    },
  });
};

export const useAddUserCustomRole = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: string) =>
      tenantAdminService.addUserCustomRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customRoleKeys.userAssignments(userId),
      });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
    },
  });
};

export const useRemoveUserCustomRole = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: string) =>
      tenantAdminService.removeUserCustomRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customRoleKeys.userAssignments(userId),
      });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
    },
  });
};

export const useReplacePlatformUserRole = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customRoleId: string | null) =>
      platformPermissionsService.replaceUserRoles(
        userId,
        customRoleId ? [customRoleId] : [],
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guesteriaAdminKeys.detail(userId),
      });
      queryClient.invalidateQueries({ queryKey: guesteriaAdminKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: customRoleKeys.lists("platform"),
      });
      queryClient.invalidateQueries({
        queryKey: customRoleKeys.platformUserAssignments(userId),
      });
    },
  });
};

export const usePlatformUserRoles = (userId: string) =>
  useQuery({
    queryKey: customRoleKeys.platformUserAssignments(userId),
    queryFn: () => platformPermissionsService.getUserRoles(userId),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(userId),
  });
